/**
 * RoteiroSection — o resultado do roteiro de validação na Conferência (projeto de agente).
 *
 * Cada caso aprovado no Gate B foi rodado contra o agente entregue e julgado pela plataforma.
 * Aqui aparece, por caso: o veredito com o modelo SIMULADO (prova o fluxo: ferramenta,
 * confirmação, limite de passos), com o modelo REAL (quando a execução pediu — avalia o
 * texto da resposta; é informativo) e, no brownfield, na BASE do cliente antes da mudança.
 *
 * "Em parte" e "só com modelo real" nunca aparecem como "passou": o que o modelo simulado
 * não consegue provar fica dito.
 *
 * `roteiro` null/undefined → não renderiza nada.
 */
import { C } from '@/theme/palette';

export const STATUS_DO_CASO = {
  passou: ['passou', C.ok],
  parcial: ['em parte', C.warn],
  falhou: ['falhou', C.bad],
  nao_rodou: ['não rodou', C.bad],
  nao_avaliado: ['só com modelo real', C.neutralMuted],
};
export const NIVEIS = [['simulado', 'Modelo simulado'], ['real', 'Modelo real'], ['base', 'Antes da mudança']];
export const ESTAGIO_DO_ROTEIRO = {
  passou: 'O agente cumpre os casos do contrato.',
  falhou: 'Há caso do contrato que o agente não cumpre.',
  nao_conferido: 'Nenhum caso pôde ser provado com o modelo simulado.',
};

const n = (v) => Number(v ?? 0).toLocaleString('pt-BR');

function Veredito({ dado }) {
  if (!dado) return <td style={{ padding: '6px 8px', color: C.neutralMuted }}>—</td>;
  const [rotulo, cor] = STATUS_DO_CASO[dado.status] ?? [dado.status, C.neutralDim];
  const reg = dado.registro ?? {};
  const temRegistro = (reg.tools_called?.length ?? 0) > 0 || reg.answer || reg.steps != null;
  return (
    <td style={{ padding: '6px 8px', verticalAlign: 'top' }}>
      <span style={{ fontSize: 11.5, fontWeight: 700, color: cor }}>{rotulo}</span>
      {dado.detalhe && (
        <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 2 }}>{dado.detalhe}</div>
      )}
      {temRegistro && (
        <details style={{ marginTop: 4 }}>
          <summary style={{ fontSize: 11, color: C.neutralDim, cursor: 'pointer' }}>O que aconteceu</summary>
          <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 4 }}>
            Ferramentas executadas: {reg.tools_called?.length ? reg.tools_called.join(', ') : 'nenhuma'}
            {reg.confirmation_requested ? ' · pediu confirmação' : ''}
            {reg.steps != null ? ` · ${n(reg.steps)} passo(s)` : ''}
          </div>
          {reg.answer && (
            <pre style={{ fontSize: 10.5, color: C.neutralMuted, whiteSpace: 'pre-wrap',
                          maxHeight: 200, overflow: 'auto', margin: '4px 0 0' }}>{reg.answer}</pre>
          )}
        </details>
      )}
    </td>
  );
}

export function RoteiroSection({ roteiro }) {
  if (!roteiro) return null;
  const casos = roteiro.casos ?? [];
  const niveis = NIVEIS.filter(([k]) => roteiro.niveis?.[k]);
  const simulado = roteiro.niveis?.simulado;
  const [, corEstagio] = { passou: ['', C.ok], falhou: ['', C.bad] }[simulado?.estagio] ?? ['', C.neutralDim];
  const regrediram = roteiro.regrediram ?? [];
  return (
    <section aria-label="Roteiro de validação" style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 14, fontWeight: 700, color: C.neutral, margin: '0 0 4px' }}>
        Roteiro de validação
      </h2>
      {simulado && (
        <p style={{ fontSize: 12, color: corEstagio, margin: '0 0 6px' }}>
          {ESTAGIO_DO_ROTEIRO[simulado.estagio] ?? simulado.estagio}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {niveis.map(([k, rotulo]) => {
          const r = roteiro.niveis[k];
          return (
            <span key={k} style={{ fontSize: 11.5, padding: '4px 10px', borderRadius: 6,
              backgroundColor: C.blackElev, border: `1px solid ${C.blackBorder}`, color: C.neutralDim }}>
              {rotulo}: <strong style={{ color: C.neutral }}>{n(r.passou + r.parcial)} de {n(r.total)}</strong>
              {r.parcial > 0 && ` · ${n(r.parcial)} em parte`}
              {r.falhou + r.nao_rodou > 0 && (
                <span style={{ color: C.bad }}> · {n(r.falhou + r.nao_rodou)} não fechou(aram)</span>
              )}
            </span>
          );
        })}
      </div>
      {regrediram.length > 0 && (
        <div role="alert" style={{ fontSize: 12.5, color: C.bad, marginBottom: 10 }}>
          {n(regrediram.length)} caso(s) passavam antes da mudança e deixaram de passar:{' '}
          {regrediram.join(', ')}
        </div>
      )}
      {roteiro.niveis?.real && (
        <p style={{ fontSize: 11.5, color: C.neutralMuted, margin: '0 0 10px' }}>
          O resultado com o modelo real é informativo: não altera o “verificado” da entrega.
        </p>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: C.neutralDim }}>Caso</th>
              {niveis.map(([k, rotulo]) => (
                <th key={k} style={{ textAlign: 'left', padding: '6px 8px', color: C.neutralDim }}>{rotulo}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {casos.map((c) => (
              <tr key={`${c.repo}:${c.id}`} style={{ borderTop: `1px solid ${C.blackBorder}` }}>
                <td style={{ padding: '6px 8px', verticalAlign: 'top' }}>
                  <span style={{ fontFamily: 'monospace', color: C.cyber }}>{c.id}</span>{' '}
                  <span style={{ color: C.neutral }}>{c.situation}</span>
                  <div style={{ fontSize: 10.5, color: C.neutralMuted }}>
                    {c.requirement ?? (c.kind === 'seguranca' || c.kind === 'limite'
                      ? 'pacote de segurança' : 'sem requisito')}
                    {c.repo ? ` · ${c.repo}` : ''}
                    {c.regrediu ? ' · regrediu' : ''}
                  </div>
                </td>
                {niveis.map(([k]) => <Veredito key={k} dado={c[k]} />)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default RoteiroSection;
